import React, { useMemo } from "react"
import {
  Box, Flex, HStack,
} from "@chakra-ui/react"
import {
  Filter, EventGroup, SegmentRuleOperator,
} from "sharedTypes"
import { generateChipId } from "utilities/chipRules"
import { computeDisabledFields, FILTER_FIELDS } from "./config"
import { normalizeOperator } from "./operatorLogic"
import useFilterOptionsContext from "./useFilterOptionsContext"
import FilterChip, { ChipSize } from "./FilterChip"
import AddFilterPopover from "./AddFilterPopover"

type Props = {
  eventId: string
  chips: Filter[]
  onChange: (next: Filter[]) => void
  eventGroups: EventGroup[]
  // The "+ Filter" / "Add Condition" trigger element. Passed verbatim to
  // AddFilterPopover so the table-view and modal can each style their own
  // trigger (pill button vs link button).
  addTrigger: React.ReactElement
  // "horizontal": chips and the add trigger wrap inline together (table view).
  // "vertical": chips wrap across multiple rows; the add trigger always sits
  // on its own row at the bottom so it never trails a half-filled row of
  // chips (modal — keeps the form layout calm).
  direction?: "horizontal" | "vertical"
  // Chip visual size. Default `md` is the dense table-view chip; modal callers
  // pass `lg` to match the form-control density of the surrounding inputs.
  size?: ChipSize
}

// Pure-state chip rendering. Both the table-view GuestFilters and the
// SegmentForm modal consume this — the table-view adds URL + segment store
// glue around it, the modal hosts it inside form-local state. Both also use
// useFilterOptionsContext so they can't drift out of sync on field support.
const ChipRow = ({
  eventId, chips, onChange, eventGroups, addTrigger, direction = "horizontal", size = "md",
}: Props) => {
  const {
    optionsContext, getConfig, fetchMoreQuestions,
  } = useFilterOptionsContext(eventId, eventGroups)

  // Any field can repeat — the BE chains rules with AND, so two identical
  // chips dedupe naturally and contradictory ones just return an empty set
  // the user can self-correct. The only disable case left is fields whose
  // dynamic option source is empty (e.g. Email Campaign before any campaign
  // has sent). Matches the table-view header's logic in GuestFilters.tsx.
  const disabledFields = useMemo(
    () => computeDisabledFields(FILTER_FIELDS, optionsContext),
    [optionsContext],
  )

  const addFilter = (field: string, values: string[], operatorOverride?: SegmentRuleOperator) => {
    const config = getConfig(field)
    if (!config) return
    const newChip: Filter = {
      id: generateChipId(),
      field,
      operator: operatorOverride
        ?? normalizeOperator(config.defaultOperator, values.length, !!config.multiSelect),
      values,
    }
    onChange([...chips, newChip])
  }

  const updateFilter = (id: string, patch: Partial<Filter>) => {
    onChange(chips.map((f) => {
      if (f.id !== id) return f
      const merged = { ...f, ...patch }
      const config = getConfig(merged.field)
      if (!config) return merged
      // If the patch touched values, re-derive the wire-level operator so it
      // stays in sync with the count (scalar eq ↔ array in, ne ↔ not_in).
      const operator = patch.values
        ? normalizeOperator(merged.operator, merged.values.length, !!config.multiSelect)
        : merged.operator

      return { ...merged, operator }
    }))
  }

  const removeFilter = (id: string) => {
    onChange(chips.filter((f) => f.id !== id))
  }

  const items = chips.map((filter) => {
    const config = getConfig(filter.field)
    if (!config) return null

    return (
      <Box key={filter.id} flexShrink={0}>
        <FilterChip
          filter={filter}
          config={config}
          context={optionsContext}
          size={size}
          onChange={(patch) => updateFilter(filter.id, patch)}
          onRemove={() => removeFilter(filter.id)}
        />
      </Box>
    )
  })

  const trigger = (
    <AddFilterPopover
      fieldConfigs={FILTER_FIELDS}
      context={optionsContext}
      disabledFields={disabledFields}
      onAdd={addFilter}
      onQuestionsScrollEnd={fetchMoreQuestions}
    >
      {addTrigger}
    </AddFilterPopover>
  )

  if (direction === "vertical") {
    return (
      <Box data-testid="chip-row">
        {items.length > 0 && (
          <Flex flexWrap="wrap" gap={5} mb={4}>
            {items}
          </Flex>
        )}
        {/* Combobox.Trigger wraps children in a block-level Box, so without
            `w="fit-content"` the Add Condition button stretches across the
            whole modal row. */}
        <Box w="fit-content">{trigger}</Box>
      </Box>
    )
  }

  return (
    <HStack spacing={2} flexWrap="wrap" align="center" data-testid="chip-row">
      {items}
      {trigger}
    </HStack>
  )
}

export default ChipRow
